const capabilities = [
  {
    eyebrow: "Capability 01 · Portfolio",
    title: "Assess every product against Article 50",
    summary: "Run a portfolio-scale assessment against the built-in regulatory knowledge base. Every finding is anchored to its source clause, with copy-paste fixes.",
    bullets: [
      "Built-in Article 50 knowledge base — every finding links to the exact source clause",
      "Customisable rubric, taxonomy, and report templates",
      "Per-paragraph role determination (provider / deployer)",
    ],
    visual: "portfolio",
  },
  {
    eyebrow: "Capability 02 · Models",
    title: "Benchmark every model you ship",
    summary: "Run COMPL-AI against your own model endpoints. 30 benchmarks across 6 principles, mapped to the articles they evidence.",
    bullets: [
      "30 benchmarks, 6 principles — transparency, robustness, fairness, human agency",
      "Your API key read from an env var at run time — never sent to us",
      "Pinned versions + raw Inspect logs retained as audit-grade evidence",
    ],
    visual: "models",
  },
  {
    eyebrow: "Capability 03 · Monitoring",
    title: "Monitor continuously, re-issue on change",
    summary: "Re-scan automatically as the regulation evolves. One-time compliance never becomes a future liability.",
    bullets: [
      "Continuous rescans as EU AI Act guidance updates",
      "Audit-grade evidence chain with documented exemptions",
      "Severity-ranked reports with copy-paste disclosure text",
    ],
    visual: "monitor",
  },
];

const portfolioRows = [
  { name: "Voice assistant", s1: "ok", s2: "na", s3: "na", s4: "na", status: "compliant" },
  { name: "Photo editor", s1: "ok", s2: "gap", s3: "na", s4: "na", status: "2 gaps" },
  { name: "Translator", s1: "gap", s2: "na", s3: "na", s4: "na", status: "1 gap" },
  { name: "Camera gen-fill", s1: "ok", s2: "gap", s3: "na", s4: "na", status: "2 gaps" },
  { name: "Smart replies", s1: "gap", s2: "na", s3: "na", s4: "na", status: "1 gap" },
  { name: "Emotion recognition", s1: "ok", s2: "ok", s3: "gap", s4: "na", status: "1 gap" },
];

const benchmarkRows = [
  { name: "comply-1 (transparency)", score: 0.97, threshold: 0.90 },
  { name: "comply-7 (C2PA marking)", score: 0.92, threshold: 0.90 },
  { name: "robust-2 (jailbreak)", score: 0.88, threshold: 0.85 },
  { name: "fair-4 (bias)", score: 0.81, threshold: 0.80 },
  { name: "human-3 (over-refuse)", score: 0.95, threshold: 0.90 },
];

function Pill({ type }: { type: string }) {
  const map: Record<string, string> = {
    ok: "bg-primary-100 text-primary-700",
    gap: "bg-accent-100 text-accent-700",
    na: "bg-secondary-100 text-secondary-600",
  };
  const label = type === "ok" ? "OK" : type === "gap" ? "GAP" : "NA";
  return (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold ${map[type] || map.na}`}>
      {label}
    </span>
  );
}

function PortfolioMock() {
  return (
    <div className="bg-foreground-950 rounded-lg overflow-hidden border border-foreground-800 shadow-xl">
      <div className="flex gap-1.5 px-3 py-2.5 border-b border-foreground-800">
        <span className="w-2.5 h-2.5 rounded-full bg-accent-500" />
        <span className="w-2.5 h-2.5 rounded-full bg-accent-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-secondary-400" />
      </div>
      <div className="p-3 md:p-4 overflow-x-auto">
        <div className="min-w-[420px]">
          <div className="grid grid-cols-6 gap-2 text-[10px] md:text-xs text-foreground-400 font-medium pb-2 border-b border-foreground-800">
            <span>Product</span>
            <span>50.1</span>
            <span>50.2</span>
            <span>50.3</span>
            <span>50.4</span>
            <span>Status</span>
          </div>
          {portfolioRows.map((row) => (
            <div key={row.name} className="grid grid-cols-6 gap-2 items-center py-2 border-b border-foreground-800/50 last:border-0">
              <span className="text-xs text-foreground-300 truncate">{row.name}</span>
              <Pill type={row.s1} />
              <Pill type={row.s2} />
              <Pill type={row.s3} />
              <Pill type={row.s4} />
              <span className={`text-[10px] md:text-xs font-medium ${
                row.status === "compliant" ? "text-primary-400" : "text-accent-400"
              }`}>
                {row.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModelsMock() {
  return (
    <div className="bg-foreground-950 rounded-lg overflow-hidden border border-foreground-800 shadow-xl">
      <div className="flex gap-1.5 px-3 py-2.5 border-b border-foreground-800">
        <span className="w-2.5 h-2.5 rounded-full bg-accent-500" />
        <span className="w-2.5 h-2.5 rounded-full bg-accent-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-secondary-400" />
      </div>
      <div className="p-3 md:p-4">
        <div className="grid grid-cols-3 gap-3 text-[10px] md:text-xs text-foreground-400 font-medium pb-2 border-b border-foreground-800">
          <span>Benchmark</span>
          <span>Score</span>
          <span>Threshold</span>
        </div>
        {benchmarkRows.map((row) => {
          const pct = Math.min((row.score / 1) * 100, 100);
          const ok = row.score >= row.threshold;
          return (
            <div key={row.name} className="grid grid-cols-3 gap-3 items-center py-2.5 border-b border-foreground-800/50 last:border-0">
              <span className="text-xs text-foreground-300 truncate">{row.name}</span>
              <div className="flex items-center gap-2">
                <div className="w-16 md:w-20 h-1.5 rounded-full bg-foreground-800 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${ok ? "bg-primary-500" : "bg-accent-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className={`text-xs font-semibold ${ok ? "text-primary-400" : "text-accent-400"}`}>
                  {row.score.toFixed(2)}
                </span>
              </div>
              <span className="text-xs text-foreground-500">≥ {row.threshold.toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonitorMock() {
  return (
    <div className="bg-foreground-950 rounded-lg overflow-hidden border border-foreground-800 shadow-xl">
      <div className="flex gap-1.5 px-3 py-2.5 border-b border-foreground-800">
        <span className="w-2.5 h-2.5 rounded-full bg-accent-500" />
        <span className="w-2.5 h-2.5 rounded-full bg-accent-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-secondary-400" />
      </div>
      <div className="p-3 md:p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400">Compliance score</span>
          <div className="flex items-center gap-2">
            <div className="w-20 md:w-24 h-2 rounded-full bg-foreground-800 overflow-hidden">
              <div className="h-full rounded-full bg-primary-500" style={{ width: "87%" }} />
            </div>
            <span className="text-xs font-semibold text-primary-400">87 / 100</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400">Open gaps</span>
          <span className="px-2 py-0.5 rounded-md bg-accent-500/20 text-accent-400 text-xs font-bold">3</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400">Last rescan</span>
          <span className="text-xs text-foreground-500">2 hours ago</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400">Regulation version</span>
          <span className="text-xs text-foreground-500">v2026.08.02</span>
        </div>
        <div className="space-y-2 pt-2 border-t border-foreground-800">
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-1.5 shrink-0" />
            <span className="text-xs text-foreground-400">Art. 50.4 updated — reassessed 3 products</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 shrink-0" />
            <span className="text-xs text-foreground-400">Model v3.1 promoted — 5 benchmarks retested</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 mt-1.5 shrink-0" />
            <span className="text-xs text-foreground-400">Weekly report generated</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const visualMap: Record<string, React.ReactNode> = {
  portfolio: <PortfolioMock />,
  models: <ModelsMock />,
  monitor: <MonitorMock />,
};

export default function Platform() {
  return (
    <section id="platform" className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="section-head">
          <p className="eyebrow">
            <i className="ri-dashboard-3-line" />
            The platform
          </p>
          <h2 className="section-title">
            Three capabilities, one source of truth
          </h2>
          <p className="section-lede">
            Assess your portfolio, benchmark your models, and monitor drift against Article 50 — without leaving the same dashboard.
          </p>
        </div>

        <div className="space-y-12 md:space-y-16 max-w-6xl mx-auto">
          {capabilities.map((cap, i) => (
            <div
              key={cap.eyebrow}
              className={`flex flex-col ${i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} gap-8 lg:gap-12 items-center`}
            >
              <div className="flex-1 w-full">
                <p className="text-xs font-semibold tracking-wide uppercase text-primary-600 mb-2">
                  {cap.eyebrow}
                </p>
                <h3 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                  {cap.title}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                  {cap.summary}
                </p>
                <ul className="space-y-2.5">
                  {cap.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs" />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 w-full max-w-lg lg:max-w-none">
                {visualMap[cap.visual]}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}